import React, { useState, useMemo, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import SpaceBetween from '@amzn/awsui-components-react-v3/polaris/space-between';
import Select, { SelectProps } from '@amzn/awsui-components-react-v3/polaris/select';
import Multiselect, { MultiselectProps } from '@amzn/awsui-components-react-v3/polaris/multiselect';
import Container from '@amzn/awsui-components-react-v3/polaris/container';
import Header from '@amzn/awsui-components-react-v3/polaris/header';
import ColumnLayout from '@amzn/awsui-components-react-v3/polaris/column-layout';
import FormField from '@amzn/awsui-components-react-v3/polaris/form-field';
import Input from '@amzn/awsui-components-react-v3/polaris/input';
import Flashbar, { FlashbarProps } from '@amzn/awsui-components-react-v3/polaris/flashbar';
import AllWorkflowsChart from 'src/components/dashboard/AllWorkflowsChart/AllWorkflowsChart';
import WorkflowDetailedCharts from 'src/components/dashboard/WorkflowDetailedCharts/WorkflowDetailedCharts';
import UserDashboardCharts from 'src/components/dashboard/UserDashboardCharts/UserDashboardCharts';
import { useWorkflowData } from 'src/components/commonComponents/WorkflowTable/useWorkflowData';
import { generateYearOptions, generateMonthOptions } from 'src/utils/dateUtils';
import { useFavoritesStore, sortWorkflowsWithTags } from 'src/stores/favoriteWorkflowsStore/favoritesStore';

export default function Dashboard() {
	const location = useLocation();
	const history = useHistory();

	const urlParams = new URLSearchParams(location.search);
	const userIdFromUrl = urlParams.get('userId') || '';

	const currentYear = new Date().getFullYear().toString();

	const ALL_YEAR = { label: 'All Year', value: '' };
	const [selectedYear, setSelectedYear] = useState<SelectProps.Option>({ label: currentYear, value: currentYear });
	const [selectedMonth, setSelectedMonth] = useState<SelectProps.Option>(ALL_YEAR);
	const [selectedWorkflows, setSelectedWorkflows] = useState<MultiselectProps.Option[]>([]);
	const [usernameInput, setUsernameInput] = useState(userIdFromUrl);
	const [activeUserId, setActiveUserId] = useState(userIdFromUrl);

	useEffect(() => {
		if (userIdFromUrl && userIdFromUrl !== activeUserId) {
			setUsernameInput(userIdFromUrl);
			setActiveUserId(userIdFromUrl);
		}
	}, [userIdFromUrl]);

	const handleUserIdChange = (userId: string) => {
		setActiveUserId(userId);
		let newUrl = location.pathname;
		const params = new URLSearchParams();
		if (userId) params.set('userId', userId);
		if (params.toString()) newUrl += `?${params.toString()}`;
		history.replace(newUrl);
	};

	const yearOptions = useMemo(() => generateYearOptions(), []);

	const monthOptions = useMemo(() => generateMonthOptions(selectedYear.value || currentYear),
		[selectedYear.value],);

	const { isLoading: workflowsLoading, error: workflowsError, allWorkflows } = useWorkflowData();

	const { workflowOptions, workflowNameMap } = useMemo(() => {
		const options: SelectProps.Option[] = [];
		const nameMap: Record<string, string> = {};
		allWorkflows.forEach((wf) => {
			options.push({ label: wf.workflowDefinitionName, value: wf.workflowId });
			nameMap[wf.workflowId] = wf.workflowDefinitionName;
		});
		return { workflowOptions: options, workflowNameMap: nameMap };
	}, [allWorkflows]);

	const { favoriteWorkflowIds } = useFavoritesStore();

	const sortedWorkflowOptions = useMemo(
		() => sortWorkflowsWithTags(workflowOptions, favoriteWorkflowIds),
		[workflowOptions, favoriteWorkflowIds]
	);

	const selectedWorkflowIds = useMemo(
		() => selectedWorkflows.map((o) => o.value!),
		[selectedWorkflows],
	);
	const isAllWorkflows = selectedWorkflowIds.length === 0;
	const yearValue = selectedYear.value || currentYear;
	const monthValue = selectedMonth.value || undefined;

	const flashbarItems = useMemo<FlashbarProps.MessageDefinition[]>(() => {
		const items: FlashbarProps.MessageDefinition[] = [];
		if (workflowsError) {
			items.push({
				type: 'error',
				header: 'Failed to load workflow types',
				content: workflowsError,
				dismissible: true,
				id: 'workflows-error',
			});
		}
		return items;
	}, [workflowsError]);

	return (
			<SpaceBetween size="l">
				{flashbarItems.length > 0 && <Flashbar items={flashbarItems} />}
				<Container
					header={<Header variant="h1" description="Real-time SNOW workflow metrics and analytics dashboard">SNOW Dashboard</Header>}
				>
					<ColumnLayout columns={4}>
						<FormField label="Year">
							<div data-testid="year-select">
								<Select
									selectedOption={selectedYear}
									onChange={({ detail }) => {
										setSelectedYear(detail.selectedOption as SelectProps.Option);
										setSelectedMonth(ALL_YEAR);
									}}
									options={yearOptions}
								/>
							</div>
						</FormField>
						<FormField label="Month">
							<div data-testid="month-select">
								<Select
									selectedOption={selectedMonth}
									onChange={({ detail }) => setSelectedMonth(detail.selectedOption as SelectProps.Option)}
									options={[ALL_YEAR, ...monthOptions]}
								/>
							</div>
						</FormField>
						<FormField label="Workflow Type">
							<Multiselect
								selectedOptions={selectedWorkflows}
								onChange={({ detail }) => setSelectedWorkflows([...detail.selectedOptions])}
								options={sortedWorkflowOptions}
								statusType={workflowsLoading ? 'loading' : 'finished'}
								loadingText="Loading workflow types..."
								filteringType="auto"
								placeholder={workflowsLoading ? 'Loading workflow types...' : 'Select workflow types'}
								disabled={workflowsLoading}
								ariaLabel="Select workflow types"
								tokenLimit={2}
							/>
						</FormField>
						<FormField label="User ID">
							<Input
								value={usernameInput}
								type="search"
								onChange={({ detail }) => {
									setUsernameInput(detail.value);
									if (!detail.value.trim()) handleUserIdChange('');
								}}
								placeholder="Enter username"
								onKeyDown={({ detail }) => {
									if (detail.key === 'Enter') handleUserIdChange(usernameInput.trim());
								}}
							/>
						</FormField>
					</ColumnLayout>
				</Container>

				{activeUserId ? (
					<UserDashboardCharts
						key={`${activeUserId}-${yearValue}-${selectedWorkflowIds.join(',')}-${monthValue ?? ''}`}
						year={yearValue}
						userId={activeUserId}
						workflowTypes={selectedWorkflowIds}
						month={monthValue}
						workflowNameMap={workflowNameMap}
					/>
				) : isAllWorkflows ? (
					<AllWorkflowsChart
						key={`${yearValue}-${monthValue ?? ''}`}
						year={yearValue}
						month={monthValue}
						workflowNameMap={workflowNameMap}
					/>
				) : (
					<WorkflowDetailedCharts
						key={`${selectedWorkflowIds.join(',')}-${yearValue}-${monthValue ?? ''}`}
						year={yearValue}
						workflowTypes={selectedWorkflowIds}
						month={monthValue}
						workflowNameMap={workflowNameMap}
					/>
				)}
			</SpaceBetween>
	);
};
